import { Box, Flex, Select, Text } from '@chakra-ui/react'
import { isNil } from 'lodash'
import { observer } from 'mobx-react-lite'
import { useEffect, useRef, useState } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'

import { Button } from '../components/Buttons/Button'
import SubHeading from '../components/Headings/SubHeading'
import { MatchNotes } from '../components/MatchNotes/MatchNotes'
import { TeamNames } from '../components/TeamNames/TeamNames'
import { db } from '../data/dexie/Database'
import { MatchResultType } from '../data/reference'
import { useMst } from '../data/stores/rootStore'
import { isCoreModeRetiredEnabled } from '../helpers/coreModeRetired'
import S3PHelpers from '../helpers/s3pHelpers'
import type { IBallStore, IClspMatchModel, IDetailedMatchStore, ISettingsModel } from '../types/models'
import type { PostMatchProps } from '../types/props'

export const PostMatch = observer(({ mode }: PostMatchProps) => {
  const navigate = useNavigate()
  const location = useLocation()
  const isScoringManually = location.pathname.search('manual') > -1
  const store = useMst()
  const {
    detailedMatches,
    balls,
    appSettings,
  }: {
    detailedMatches: IDetailedMatchStore
    balls: IBallStore
    appSettings: ISettingsModel
  } = store
  const { id } = useParams()
  if (!id) return null
  detailedMatches.getMatch(id, mode)
  const game: IClspMatchModel | undefined = detailedMatches.results.get(id)?.match
  const playerOfMatch = game?.getPlayerOfMatch
  const [isCompletingPostMatchMode, setIsCompletingPostMatchMode] = useState(false)
  const [publicationError, setPublicationError] = useState(false)
  const completionInProgress = useRef(false)
  const finalStatusEnqueue = useRef<Promise<unknown>>()
  const mounted = useRef(true)
  useEffect(() => {
    mounted.current = true
    return () => {
      mounted.current = false
    }
  }, [])
  const coreRetired = isCoreModeRetiredEnabled()
  const isActingAsCore = (mode === 'core' && !coreRetired) || (coreRetired && mode === 'advanced')

  const handlePlayerOfMatchChange: React.ChangeEventHandler<HTMLSelectElement> = event => {
    if (game && event.target.value) {
      game.setPlayerOfMatch(event.target.value)
      if ((isActingAsCore || isScoringManually) && !isNil(game.matchResultTypeId)) {
        // update matchResult S3P message with our Player of the Match
        const winningTeam = !isNil(game.winningMatchTeamId) ? game.getTeamById(game.winningMatchTeamId) : null
        db.createS3PMessage(
          S3PHelpers.metadata(isScoringManually ? 'postMatch' : mode, game),
          S3PHelpers.matchResult(
            MatchResultType[game.matchResultTypeId],
            winningTeam || null,
            game,
            game.getPlayerOfMatch?.player.id,
            isScoringManually
          )
        )
      }
    }
  }

  const handleCompleteGame = async () => {
    if (!game || completionInProgress.current) return
    const ownsCompletion = () => mounted.current && store.socketStore.matchId === game.id
    completionInProgress.current = true
    setIsCompletingPostMatchMode(true)
    setPublicationError(false)
    try {
      if (isActingAsCore || isScoringManually) {
        if (!finalStatusEnqueue.current) {
          finalStatusEnqueue.current = db
            .createS3PMessage(
              S3PHelpers.metadata(isScoringManually ? 'postMatch' : mode, game),
              S3PHelpers.matchStatus('CONFIRMED', game)
            )
            .catch(error => {
              finalStatusEnqueue.current = undefined
              throw error
            })
        }
        await finalStatusEnqueue.current
      }
      if (!mounted.current) return
      if (!ownsCompletion()) throw new Error('The scoring connection belongs to another match')
      if (isScoringManually) appSettings.manualScoring.setForceSync(true)
      game.submitMatch()
      await store.flushMatchPublication(game.id)
      if (!ownsCompletion()) return
      if (isScoringManually) appSettings.manualScoring.setActive(false)
      navigate('/games')
    } catch {
      if (mounted.current) setPublicationError(true)
    } finally {
      if (isScoringManually && ownsCompletion()) appSettings.manualScoring.setForceSync(false)
      completionInProgress.current = false
      if (mounted.current) setIsCompletingPostMatchMode(false)
    }
  }

  if (!game) return <Text>Missing the Game</Text>

  return (
    <Flex
      h="100%"
      marginLeft={['0', '0', '14px', '14px']}
      w={mode !== 'advanced' ? ['100%', '100%', 'auto', 'auto'] : undefined}
    >
      <Flex flex="10" direction="column" w={mode !== 'advanced' ? ['100%', '100%', 'auto', 'auto'] : undefined}>
        <TeamNames game={game} mode={mode} />
        <Flex
          as="fieldset"
          disabled={isCompletingPostMatchMode}
          minWidth={0}
          flex={1}
          direction="column"
          bg="white"
          borderTopLeftRadius={mode === 'advanced' ? '7px' : ['0px', '0px', '7px', '7px']}
          borderTopRightRadius={mode === 'advanced' ? '7px' : ['0px', '0px', '7px', '7px']}
          padding={mode === 'advanced' ? '14px 21px 0' : '14px 14px 0'}
        >
          <Flex direction="column" marginBottom="37px">
            <Box marginBottom="40px">
              <SubHeading text="Post-Match" />
              <Text data-testid="gameDescription">{game.description}</Text>
            </Box>
            <Box marginBottom="40px">
              <SubHeading text="Player Of The Match" />
              <Select
                maxWidth="400px"
                onChange={handlePlayerOfMatchChange}
                value={playerOfMatch ? playerOfMatch.id : 'default'}
              >
                <option value="default">-- Please Select --</option>
                <optgroup label={game.getHomeTeam()?.name || ''}>
                  {game.getHomeTeam()?.matchPlayers.map(mPlayer => (
                    <option key={mPlayer.id} value={mPlayer.id}>
                      {mPlayer.getFullName}
                    </option>
                  ))}
                </optgroup>
                <optgroup label={game.getAwayTeam?.name || ''}>
                  {game.getAwayTeam?.matchPlayers.map(mPlayer => (
                    <option key={mPlayer.id} value={mPlayer.id}>
                      {mPlayer.getFullName}
                    </option>
                  ))}
                </optgroup>
              </Select>
            </Box>
            <Box>
              <SubHeading text="Match Notes" />
              <MatchNotes game={game} notes={game.matchNotes} balls={balls} isScoringManually={isScoringManually} />
              {publicationError && (
                <Text role="alert" color="red.600" marginTop="14px">
                  Publication is not complete. Keep this page open and retry when connected. Your data remains queued.
                </Text>
              )}
              <Flex marginTop="20px" justifyContent="center">
                <Button
                  size="lg"
                  colorScheme="blue"
                  onClick={handleCompleteGame}
                  data-testid="completeMatchButton"
                  isDisabled={isCompletingPostMatchMode}
                >
                  {isCompletingPostMatchMode ? 'Publishing...' : 'Complete Match'}
                </Button>
              </Flex>
            </Box>
          </Flex>
        </Flex>
      </Flex>
      <Flex
        flex="2"
        direction="column"
        display={mode !== 'advanced' ? ['none', 'none', 'flex', 'flex'] : undefined}
      ></Flex>
    </Flex>
  )
})
